import { useMemo } from 'react';
import { MetricChart } from './charts';
import { gameLabel } from './games';

/**
 * GameReviewsChart — post-game ratings (GET /api/ratings/historical): a trend line per rating
 * when there are two or more reviews, then the latest reviews as rows.
 *
 * reviews: [{ name, lastPlayDate, timeStamp, happiness, excitement, control, pain }]
 */
const toMs = (x) => {
    if (x == null || x === '') return null;
    if (typeof x === 'number') return x;
    const n = Number(x);
    if (Number.isFinite(n)) return n;
    const p = Date.parse(x);
    return Number.isFinite(p) ? p : null;
};
const num = (x) => (x == null || x === '' ? NaN : Number(x));
const fmtDay = (ms) => {
    if (!ms) return 'Date not recorded';
    const d = new Date(ms);
    return d.toLocaleDateString([], d.getFullYear() === new Date().getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
};
const METRICS = [
    { key: 'happiness', label: 'Happiness' },
    { key: 'excitement', label: 'Excitement' },
    { key: 'control', label: 'Control' },
    { key: 'pain', label: 'Pain' },
];

const S = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 },
    list: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    item: { display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 0', borderTop: '1px solid var(--egdl-hairline)' },
    head: { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 12px' },
    game: { flex: 1, minWidth: 0, fontWeight: 600, fontSize: 19, overflowWrap: 'anywhere' },
    when: { fontSize: 15, color: 'var(--egdl-text-secondary)', fontVariantNumeric: 'tabular-nums' },
    vals: { display: 'flex', flexWrap: 'wrap', gap: '8px 24px', margin: 0 },
    pair: { display: 'inline-flex', alignItems: 'baseline', gap: 8 },
    k: { fontWeight: 600, fontSize: 14, letterSpacing: 1.7, textTransform: 'uppercase', color: 'var(--egdl-text-secondary)' },
    v: { fontWeight: 600, fontSize: 17, fontVariantNumeric: 'tabular-nums', margin: 0 },
    caps: { fontWeight: 600, fontSize: 14, letterSpacing: 1.7, textTransform: 'uppercase', color: 'var(--egdl-text-secondary)' },
    empty: { padding: 24, background: 'var(--egdl-surface-sunken)', fontSize: 17, lineHeight: 1.5, color: 'var(--egdl-text-secondary)' },
};

function GameReviewsChart({ reviews, emptyText = 'No game reviews yet' }) {
    const items = useMemo(() => (Array.isArray(reviews) ? reviews : [])
        .map((r, i) => ({
            i,
            game: r?.name || 'Unnamed game',
            t: toMs(r?.timeStamp) || toMs(r?.lastPlayDate),
            vals: Object.fromEntries(METRICS.map((m) => [m.key, num(r?.[m.key])])),
        }))
        .filter((r) => METRICS.some((m) => Number.isFinite(r.vals[m.key])))
        .sort((a, b) => (a.t || 0) - (b.t || 0) || a.i - b.i), [reviews]);

    if (!items.length) return <div style={S.empty}>{emptyText}</div>;

    const used = METRICS.filter((m) => items.some((r) => Number.isFinite(r.vals[m.key])));
    const latest = [...items].reverse().slice(0, 5);

    return (
        <div style={S.wrap}>
            {items.length > 1 ? (
                <MetricChart
                    series={used.map((m, k) => ({ label: m.label, dp: 1, idx: k, data: items.map((r) => (Number.isFinite(r.vals[m.key]) ? r.vals[m.key] : null)) }))}
                    xLabels={items.map((r) => (r.t ? fmtDay(r.t) : '—'))}
                    duration={Math.max(1, items.length - 1)}
                    height={240}
                    brush={items.length > 24}
                />
            ) : null}
            <div style={S.list}>
                <span style={{ ...S.caps, paddingBottom: 8 }}>{items.length > 1 ? 'Latest reviews' : 'Review'}</span>
                {latest.map((r) => (
                    <div key={r.i} style={S.item}>
                        <div style={S.head}>
                            <span style={S.game}>{gameLabel(r.game, r.game)}</span>
                            <span style={S.when}>{fmtDay(r.t)}</span>
                        </div>
                        <dl style={S.vals}>
                            {used.map((m) => (
                                <div key={m.key} style={S.pair}>
                                    <dt style={S.k}>{m.label}</dt>
                                    <dd style={S.v}>{Number.isFinite(r.vals[m.key]) ? +r.vals[m.key].toFixed(1) : '—'}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default GameReviewsChart;
